import { type SyntheticEvent } from "react";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { mergeSx } from "ui/merge-sx";
import { Switch } from "ui/switch";
import { Tab, Tabs } from "ui/tabs";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import { useCurrentUser } from "hooks/api/use-current-user";
import {
  useTouchpointVersion,
  type TouchpointVersionAttributes,
} from "hooks/api/use-touchpoint-versions";

import { hasPermission } from "utilities/user";
import { useHandleEditorOpen } from "containers/admin/clients/touchpoint/components/builder/use-handle-editor-open";
import { useTouchpointLocking } from "contexts/touchpoint-locking-context";
import { Permission } from "types/auth";
import { isLandingPage } from "types/typeguards";
import LockingModal from "components/modals/locking-modal/locking-modal";
import { Button } from "ui/button";

import { TouchpointStatuses } from "types";

export const builderPreviewMenuOptions = {
  mobileView: "Mobile",
  desktopView: "Desktop",
  htmlView: "HTML",
} as const;

export type BuilderPreviewViews =
  (typeof builderPreviewMenuOptions)[keyof typeof builderPreviewMenuOptions];

/**
 * Equal outer tracks put the view toggle on the row's centre line, which is where the preview frame
 * below it is centred too. The flex `space-between` this replaced offset the toggle by half the
 * difference between the editor button and the switches.
 */
const ROW_SX: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: "1fr auto 1fr",
  justifyItems: "start",
  alignItems: "center",
  width: "100%",
  mb: 1,
};

const BUILDER_ROW_SX: SxProps<Theme> = mergeSx(ROW_SX, { mb: 4 });

const HIDDEN_EDITOR_BUTTON_SX: SxProps<Theme> = { visibility: "hidden" };

interface BuilderPreviewHeaderProps {
  currentView: BuilderPreviewViews;
  selectedVersion?: TouchpointVersionAttributes;
  setCurrentView: (string: BuilderPreviewViews) => void;
  isCommentMode: boolean;
  setIsCommentMode: (isCommentMode: boolean) => void;
  isCommentModeDisabled?: boolean;
  isTouchpointPreview: boolean;
  isFullContentMode?: boolean;
  /**
   * Omit if the preview does not honour Full content at all. Supplying it is not enough to show the switch —
   * the header shows it on web page touchpoints only.
   */
  setIsFullContentMode?: (isFullContentMode: boolean) => void;
}

export const BuilderPreviewPaneHeader = ({
  currentView,
  selectedVersion,
  setCurrentView,
  isCommentMode,
  setIsCommentMode,
  isCommentModeDisabled = false,
  isTouchpointPreview = false,
  isFullContentMode = false,
  setIsFullContentMode,
}: BuilderPreviewHeaderProps) => {
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();
  const currentUser = useCurrentUser();

  const isFullContentSupported = !!setIsFullContentMode && isLandingPage(currentTouchpoint);
  const isClientUser = hasPermission(currentUser, Permission.PERM_CLIENT_USER);
  const { isLockingModalOpen } = useTouchpointLocking();
  const isPublished = selectedVersion?.status === TouchpointStatuses.PUBLISHED;
  const isEditorButtonHidden = !isTouchpointPreview || isClientUser || isPublished;

  // Shares the preview route's version-detail cache entry, so this costs no extra request. Only the
  // touchpoint preview needs it — inside the builder, `selectedVersion` is already the authoritative record.
  const { data: latestVersion } = useTouchpointVersion(
    {
      clientId: currentClient.id,
      campaignId: currentCampaign.id,
      touchpointId: currentTouchpoint.id,
      id: selectedVersion?.id,
    },
    { enabled: isTouchpointPreview },
  );

  const { handleEditorOpen } = useHandleEditorOpen({
    selectedVersion,
    latestVersion: latestVersion ?? null,
  });

  // Comment mode is already the stricter of the two: it renders statically AND blocks links, so Full content
  // has nothing left to add, and its switch is hidden for as long as comments are on. Reset it on the way in
  // regardless — otherwise turning comments back off would bring the control back still checked, describing
  // a render the user is no longer looking at.
  const handleCommentModeChange = (nextIsCommentMode: boolean) => {
    setIsCommentMode(nextIsCommentMode);

    if (nextIsCommentMode) {
      setIsFullContentMode?.(false);
    }
  };

  const handleViewChange = (_event: SyntheticEvent, view: BuilderPreviewViews) => {
    setCurrentView(view);
  };

  const { mobileView, desktopView, htmlView } = builderPreviewMenuOptions;

  const viewToggleOptions: BuilderPreviewViews[] = [mobileView, desktopView];

  if (!isClientUser) {
    // Clients don't see the HTML view, everyone else does
    viewToggleOptions.push(htmlView);
  }

  const selectedView = viewToggleOptions.includes(currentView) ? currentView : false;

  return (
    <Box sx={isTouchpointPreview ? ROW_SX : BUILDER_ROW_SX}>
      <Button
        data-testid="open-editor-button"
        size="medium"
        variant="outlined"
        sx={isEditorButtonHidden ? HIDDEN_EDITOR_BUTTON_SX : undefined}
        onClick={() => handleEditorOpen("design")}>
        Open in editor
      </Button>
      {isLockingModalOpen && <LockingModal />}
      <Tabs aria-label="preview-toggle" value={selectedView} onChange={handleViewChange}>
        {viewToggleOptions.map((view) => (
          <Tab key={view} value={view} label={view} />
        ))}
      </Tabs>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifySelf: "end",
          gap: 2,
          visibility: currentView === htmlView ? "hidden" : "visible",
        }}>
        {isFullContentSupported && !isCommentMode && (
          <Switch
            toggleName="Full content"
            checked={isFullContentMode}
            onChange={setIsFullContentMode}
          />
        )}
        <Switch
          toggleName="Comment mode"
          checked={isCommentMode}
          onChange={handleCommentModeChange}
          disabled={isCommentModeDisabled}
        />
      </Box>
    </Box>
  );
};
